import { Modal, ModalFullscreen, useDeviceType } from '@cian/ui-kit';
import { RoundButton } from '@cian/ui-kit/button';
import { IconClose16 } from '@cian/ui-kit/icons';
import { FC, useCallback, useEffect, useState } from 'react';

import { useApplicationContext } from 'shared/utils/applicationContext';

import style from './AuthModal.css';
import { reloadPage } from './utils';

import type { IAuthenticationContext } from '@cian/authentication-widget';

type TAuthenticationWidgetLib = typeof import('@cian/authentication-widget');

interface IAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface IAuthState {
  context: IAuthenticationContext;
  lib: TAuthenticationWidgetLib;
}

export const AuthModal: FC<IAuthModalProps> = ({ isOpen, onClose }) => {
  const isMobile = useDeviceType() !== 'desktop';
  const {
    config,
    httpApi,
    logger,
    custom: { subdomain },
  } = useApplicationContext();
  const [auth, setAuth] = useState<IAuthState | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setAuth(null);

      return;
    }

    let isCancelled = false;
    let cleanup: VoidFunction = () => {};

    const loadAuthenticationWidget = async () => {
      try {
        const lib = await import('@cian/authentication-widget');

        if (isCancelled) {
          return;
        }

        const context = lib.createContext(
          { config, httpApi, logger, subdomain },
          {
            view: { active: lib.EActiveView.AuthenticationRegistration },
            activeFlow: null,
            isRememberAccountsFlow: false,
            source: 'landing',
          },
        );
        const reload = () => reloadPage();
        const successEvents = [
          lib.EEventType.AuthenticateSucceed,
          lib.EEventType.SocialAuthenticateSucceed,
          lib.EEventType.RegisterSucceed,
          lib.EEventType.EmailSentClosed,
        ];

        successEvents.forEach(event => context.on(event, reload));
        cleanup = () => successEvents.forEach(event => context.off(event, reload));

        setAuth({ lib, context });
      } catch (error) {
        if (!isCancelled) {
          logger.error(error, {
            domain: 'superhost.authWidget.load',
          });
        }
      }
    };

    void loadAuthenticationWidget();

    return () => {
      isCancelled = true;
      cleanup();
    };
  }, [config, httpApi, isOpen, logger, subdomain]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const content = auth && (
    <auth.lib.Provider context={auth.context}>
      <auth.lib.Authentication disabledSocials={Object.values(auth.lib.ESocialType)} frameless logger={logger} />
    </auth.lib.Provider>
  );

  if (isMobile) {
    return (
      <ModalFullscreen open={isOpen} onClose={onClose} portal={false} theme="white">
        <div className={style['mobile-header']}>
          <RoundButton
            icon={<IconClose16 color="current_color" />}
            size="XS"
            theme="fill_dark"
            title="Закрыть"
            onClick={onClose}
          />
        </div>
        <div className={style['mobile-content']}>{content}</div>
      </ModalFullscreen>
    );
  }

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="S"
      closeButtonProps={{
        icon: <IconClose16 color="current_color" />,
        size: 'XS',
        theme: 'dark',
        title: 'Закрыть',
      }}
    >
      <div className={style['desktop-content']}>{content}</div>
    </Modal>
  );
};
